import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext'; // Import context mới
import { PendingRequestsProvider } from './contexts/PendingRequestsContext';
import { TaskNotificationProvider } from './contexts/TaskNotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ManagerRoute from './components/ManagerRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import PageLoading from './components/common/PageLoading';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
// Trang xem thử biểu đồ, chỉ nạp ở môi trường development (không cần đăng nhập)
const DevChartPreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/DevChartPreview')) : null;
// Trang xem thử giao diện đăng ký làm thêm giờ (chỉ development, không cần đăng nhập)
const DevOvertimePreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/DevOvertimePreview')) : null;
const DevEarlyLeavePreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/DevEarlyLeavePreview')) : null;
// Trang xem thử modal chuyển khoản lương + sheet mở app ngân hàng (chỉ development, không cần đăng nhập)
const DevTransferPreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/DevTransferPreview')) : null;

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminRaceTrackPage = lazy(() => import('./pages/AdminRaceTrackPage'));
const AdminRequestsPage = lazy(() => import('./pages/AdminRequestsPage'));
const AdminOvertimePage = lazy(() => import('./pages/AdminOvertimePage'));
const SchedulePage = lazy(() => import('./pages/SchedulePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ReportPage = lazy(() => import('./pages/ReportPage'));

const TaskManagementPage = lazy(() => import('./pages/TaskManagementPage'));
const MyTasksPage = lazy(() => import('./pages/MyTasksPage'));
const TaskTypeManagementPage = lazy(() => import('./pages/TaskTypeManagementPage'));
const SalaryManagementPage = lazy(() => import('./pages/SalaryManagementPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const IncomePage = lazy(() => import('./pages/IncomePage'));
const WorkTermsPage = lazy(() => import('./pages/WorkTermsPage'));
const AttendanceSystemPage = lazy(() => import('./pages/AttendanceSystemPage'));
const AttendanceListPage = lazy(() => import('./pages/AttendanceListPage'));
const MyAttendancePage = lazy(() => import('./pages/MyAttendancePage'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'));

const theme = createTheme({
  palette: {
    background: {
      default: '#f4f6f8'
    }
  }
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SettingsProvider>
          <PendingRequestsProvider>
            <TaskNotificationProvider>
              <Suspense fallback={<PageLoading />}>
                <Routes>
                  {DevChartPreview && <Route path="/dev/chart-preview" element={<DevChartPreview />} />}
                  {DevOvertimePreview && <Route path="/dev/overtime-preview" element={<DevOvertimePreview />} />}
                  {DevEarlyLeavePreview && <Route path="/dev/early-leave-preview" element={<DevEarlyLeavePreview />} />}
                  {DevTransferPreview && <Route path="/dev/transfer-preview" element={<DevTransferPreview />} />}
                  <Route
                    path="/login"
                    element={<LoginPage />}
                  />

                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <DashboardPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/schedule"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <SchedulePage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/report"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <ReportPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <ProfilePage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-tasks"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <MyTasksPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/work-terms"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <WorkTermsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/income"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <IncomePage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-attendance"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <MyAttendancePage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <AdminRoute>
                        <DashboardLayout>
                          <AdminPage />
                        </DashboardLayout>
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/race-track"
                    element={
                      <AdminRoute>
                        <DashboardLayout>
                          <AdminRaceTrackPage />
                        </DashboardLayout>
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/requests"
                    element={
                      <ManagerRoute>
                        <DashboardLayout>
                          <AdminRequestsPage />
                        </DashboardLayout>
                      </ManagerRoute>
                    }
                  />
                  <Route
                    path="/admin/overtime"
                    element={
                      <ManagerRoute>
                        <DashboardLayout>
                          <AdminOvertimePage />
                        </DashboardLayout>
                      </ManagerRoute>
                    }
                  />
                  <Route
                    path="/admin/tasks"
                    element={
                      <AdminRoute>
                        <DashboardLayout>
                          <TaskManagementPage />
                        </DashboardLayout>
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/task-types"
                    element={
                      <AdminRoute>
                        <DashboardLayout>
                          <TaskTypeManagementPage />
                        </DashboardLayout>
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/salary"
                    element={
                      <AdminRoute>
                        <DashboardLayout>
                          <SalaryManagementPage />
                        </DashboardLayout>
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/payment"
                    element={
                      <AdminRoute>
                        <DashboardLayout>
                          <PaymentPage />
                        </DashboardLayout>
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/attendance-system"
                    element={
                      <AdminRoute>
                        <DashboardLayout>
                          <AttendanceSystemPage />
                        </DashboardLayout>
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/attendance"
                    element={
                      <AdminRoute>
                        <DashboardLayout>
                          <AttendanceListPage />
                        </DashboardLayout>
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/notification-settings"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <NotificationSettingsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </TaskNotificationProvider>
          </PendingRequestsProvider>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
